/**
 * DSM (Device State Monitor) backend client.
 *
 * Talks to the dsm-service whose OpenAPI is in
 * `portal-ui/dsm-openapi-json/dsm-openapi.json`. The service runs as a
 * separate deployment from davinci-management, so it has its own base URL.
 *
 * Same same-origin proxy pattern as `checkout/env.js` (pair-api):
 *   • dev   — Vite proxy in `vite.config.js` rewrites `/dsm-api/*` to the
 *             real DSM host (avoids browser CORS).
 *   • prod  — nginx in `portal-ui/nginx.conf` proxies
 *             `/portal-ui/dsm-api/*` to the same host.
 *
 * Override the host with `VITE_DSM_API_BASE_URL` if needed.
 */
import axios from 'axios';

const isLocalDev =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

const PROD_BASE = `${(import.meta.env.BASE_URL || '/').replace(/\/$/, '')}/dsm-api`;
const DEFAULT_BASE = isLocalDev ? '/dsm-api' : PROD_BASE;

const BASE_URL = (import.meta.env.VITE_DSM_API_BASE_URL ?? DEFAULT_BASE).replace(/\/$/, '');

const client = axios.create({
  baseURL: BASE_URL,
  timeout: 20000,
});

client.interceptors.response.use(
  (r) => r,
  (err) => {
    const msg =
      err?.response?.data?.detail?.[0]?.msg ||
      err?.response?.data?.message ||
      err?.message ||
      'DSM request failed';
    return Promise.reject(new Error(msg));
  }
);

// ---------------------------------------------------------------------------
// ⚠️  TEMPORARY DEMO HACK — REMOVE AFTER DEMO ⚠️
// ---------------------------------------------------------------------------
// Forces a fixed paired-user name for two specific demo devices so the DSM
// page always shows them as paired, regardless of the real device state.
//
// This is intentionally applied in the service layer so the table row, the
// detail drawer, the "Paired with User" KPI, and the client-side search all
// stay consistent (they share these device objects).
//
// TO REVERT: delete this whole block and the three `applyDemoPairedUser(...)`
// calls in listDeviceStatuses(), getDeviceStatus(), and
// listDeviceStatusesByOrgSite() below.
const DEMO_PAIRED_USER_BY_MAC = {
  '017e6b27f8ff': 'Charlie Forsyth',
  '312cc316abd1': 'Craig Miller',
};

// Normalize a MAC for comparison: strip any non-hex separators (':', '-', '.')
// and lowercase, so '01:7E:6B:27:F8:FF' and '017e6b27f8ff' match the same key.
function normalizeMacForDemo(mac) {
  return String(mac ?? '').replace(/[^0-9a-fA-F]/g, '').toLowerCase();
}

function applyDemoPairedUser(device) {
  if (!device) return device;
  const override = DEMO_PAIRED_USER_BY_MAC[normalizeMacForDemo(device.mac)];
  return override ? { ...device, pairedUser: override } : device;
}
// ---------------------------------------------------------------------------
// ⚠️  END TEMPORARY DEMO HACK ⚠️
// ---------------------------------------------------------------------------

/**
 * GET /dsm/device-status
 * Returns DeviceStatusListResponse: { items, total, page, pageSize, pages }.
 *
 * Pass only the filter values you want applied; nullish/empty values are
 * dropped from the query string so they don't accidentally narrow results.
 */
export async function listDeviceStatuses({
  org,
  site,
  online,
  inRack,
  pairedUser,
  page = 1,
  pageSize = 50,
} = {}) {
  const params = { page, page_size: pageSize };
  if (org && org !== '') params.org = org;
  if (site && site !== '') params.site = site;
  if (online !== undefined && online !== null) params.online = online;
  if (inRack !== undefined && inRack !== null) params.in_rack = inRack;
  if (pairedUser && pairedUser !== '') params.paired_user = pairedUser;

  const { data } = await client.get('/dsm/device-status', { params });
  // DEMO HACK: override paired user for specific demo devices (see top of file).
  if (data && Array.isArray(data.items)) {
    data.items = data.items.map(applyDemoPairedUser);
  }
  return data;
}

/** GET /dsm/device-status/{mac} */
export async function getDeviceStatus(mac) {
  const { data } = await client.get(`/dsm/device-status/${encodeURIComponent(mac)}`);
  // DEMO HACK: override paired user for specific demo devices (see top of file).
  return applyDemoPairedUser(data);
}

/** GET /dsm/device-status/org/{org}/site/{site} — returns DeviceStatusResponse[] */
export async function listDeviceStatusesByOrgSite(org, site) {
  const { data } = await client.get(
    `/dsm/device-status/org/${encodeURIComponent(org)}/site/${encodeURIComponent(site)}`
  );
  // DEMO HACK: override paired user for specific demo devices (see top of file).
  return Array.isArray(data) ? data.map(applyDemoPairedUser) : data;
}

/** GET /dsm/health */
export async function getDsmHealth() {
  const { data } = await client.get('/dsm/health');
  return data; // { status, database }
}

/** GET /dsm/ready */
export async function getDsmReadiness() {
  const { data } = await client.get('/dsm/ready');
  return data; // { ready, database }
}

export default {
  listDeviceStatuses,
  listDeviceStatusesByOrgSite,
  getDeviceStatus,
  getDsmHealth,
  getDsmReadiness,
};

